// Space weather feeds, read into one compact summary. Shared by the API
// (api/spaceweather.js), the snapshot script
// (scripts/build-spaceweather-snapshot.mjs), the viewer and the pages, so
// every reader sees the same numbers. Pure: no fetching here.
//
// Sources, all public (NOAA SWPC, services.swpc.noaa.gov; NASA SDO through
// Helioviewer). The formats were read from the live feeds in October 2026;
// each parser also takes the older array-of-arrays layout NOAA used before,
// and returns null rather than throwing on anything it cannot read.
//
//   products/noaa-planetary-k-index-forecast.json  Kp, 3-hour blocks: the
//       last week observed, today estimated, three days predicted
//   products/noaa-planetary-k-index.json           Kp, observed only (backup)
//   json/ovation_aurora_latest.json                OVATION Prime nowcast, a
//       1 x 1 degree geographic grid of aurora probability (0 to 100)
//   products/alerts.json                           watches, warnings, alerts
//   products/summary/solar-wind-speed.json,
//   products/summary/solar-wind-mag-field.json     the latest L1 readings
//   json/rtsw/rtsw_wind_1m.json, rtsw_mag_1m.json  a day of 1-minute L1 data
//   json/solar_regions.json                        NOAA numbered active regions
//   json/goes/primary/xray-flares-7-day.json       GOES X-ray flares
//   json/goes/primary/xray-flares-latest.json      the current X-ray level
//   json/edited_events.json                        flare-to-region matches

const num = (v) => (v === null || v === undefined || v === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null);
const iso = (s) => {
  if (!s) return null;
  const t = Date.parse(/Z$|[+-]\d\d:?\d\d$/.test(s) ? s : `${String(s).replace(' ', 'T')}Z`);
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
};

// Rows as objects, whether NOAA sent objects or a header row of arrays.
function rowsOf(list) {
  if (!Array.isArray(list) || !list.length) return [];
  if (!Array.isArray(list[0])) return list.filter((r) => r && typeof r === 'object');
  const head = list[0].map((h) => String(h));
  return list.slice(1).map((r) => Object.fromEntries(head.map((h, i) => [h, r[i]])));
}

// NOAA's geomagnetic storm scale: G1 at Kp 5 up to G5 at Kp 9
// (swpc.noaa.gov/noaa-scales-explanation).
export function gScale(kp) {
  if (!(kp >= 5)) return null;
  return `G${Math.min(5, Math.floor(kp + 1e-9) - 4)}`;
}

// Kp in 3-hour blocks: [{ t (block start, ISO), kp, kind }], kind
// 'observed' | 'estimated' | 'predicted', oldest first.
export function parseKp(forecast, observed) {
  const out = new Map();
  for (const r of rowsOf(observed)) {
    const t = iso(r.time_tag);
    const kp = num(r.Kp ?? r.kp);
    if (t && kp !== null) out.set(t, { t, kp: Math.round(kp * 100) / 100, kind: 'observed' });
  }
  for (const r of rowsOf(forecast)) {
    const t = iso(r.time_tag);
    const kp = num(r.kp ?? r.Kp);
    if (!t || kp === null) continue;
    const kind = r.observed === 'observed' ? 'observed' : r.observed === 'estimated' ? 'estimated' : 'predicted';
    if (out.has(t) && out.get(t).kind === 'observed' && kind !== 'observed') continue;
    out.set(t, { t, kp: Math.round(kp * 100) / 100, kind });
  }
  const list = [...out.values()].sort((a, b) => a.t.localeCompare(b.t));
  return list.length ? list : null;
}

// The Kp block holding a moment (3-hour blocks from their start).
export function kpAt(series, ms) {
  if (!Array.isArray(series)) return null;
  for (let i = series.length - 1; i >= 0; i--) {
    const t = Date.parse(series[i].t);
    if (ms >= t && ms < t + 3 * 3600 * 1000) return series[i];
  }
  return null;
}

// The highest Kp over [fromMs, toMs) and the block it falls in.
export function kpMax(series, fromMs, toMs, kinds = null) {
  let best = null;
  for (const b of series || []) {
    const t = Date.parse(b.t);
    if (t + 3 * 3600 * 1000 <= fromMs || t >= toMs) continue;
    if (kinds && !kinds.includes(b.kind)) continue;
    if (!best || b.kp > best.kp) best = b;
  }
  return best;
}

// The OVATION grid packed for the browser: per hemisphere, rows of
// magnitude 40..89 degrees (geographic) by 360 longitudes from 0 east, one
// character per cell from a 64-symbol alphabet, value = index / 63 * 100.
export const OVATION_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_';
export const OVATION_LAT_MIN = 40;
export const OVATION_LAT_MAX = 89;

export function parseOvation(json) {
  if (!json || !Array.isArray(json.coordinates) || json.coordinates.length < 1000) return null;
  const grid = new Float32Array(360 * 181);
  for (const c of json.coordinates) {
    if (!Array.isArray(c) || c.length < 3) continue;
    const lon = ((Math.round(c[0]) % 360) + 360) % 360;
    const lat = Math.round(c[1]);
    if (lat < -90 || lat > 90) continue;
    grid[(lat + 90) * 360 + lon] = Math.max(0, Math.min(100, Number(c[2]) || 0));
  }
  const pack = (sign) => {
    const rows = [];
    for (let a = OVATION_LAT_MIN; a <= OVATION_LAT_MAX; a++) {
      const base = (sign * a + 90) * 360;
      let s = '';
      for (let lon = 0; lon < 360; lon++) s += OVATION_ALPHABET[Math.round((grid[base + lon] / 100) * 63)];
      rows.push(s);
    }
    return rows;
  };
  let max = 0;
  for (const v of grid) if (v > max) max = v;
  return {
    observed: iso(json['Observation Time']),
    forecast: iso(json['Forecast Time']),
    max,
    north: pack(1),
    south: pack(-1),
    grid, // the full grid, for the server's own use; dropped before sending
  };
}

// Unpack one hemisphere of a packed grid into a lookup (lat, lonEast) -> value.
export function ovationLookup(ov) {
  if (!ov || !ov.north) return null;
  const code = new Map([...OVATION_ALPHABET].map((ch, i) => [ch, (i / 63) * 100]));
  return (lat, lon) => {
    const a = Math.round(Math.abs(lat));
    if (a < OVATION_LAT_MIN || a > OVATION_LAT_MAX) return 0;
    const rows = lat >= 0 ? ov.north : ov.south;
    const row = rows[a - OVATION_LAT_MIN];
    const l = ((Math.round(lon) % 360) + 360) % 360;
    return row ? code.get(row[l]) || 0 : 0;
  };
}

// Watches, warnings and alerts: the headline and the text, newest first.
export function parseAlerts(list, { now = Date.now(), days = 3, max = 12 } = {}) {
  if (!Array.isArray(list)) return null;
  const out = [];
  for (const r of list) {
    const issued = iso(r.issue_datetime);
    if (!issued || now - Date.parse(issued) > days * 86400000) continue;
    const text = String(r.message || '').replace(/\r/g, '').trim();
    const lines = text.split('\n').map((l) => l.trim());
    const code = (text.match(/Space Weather Message Code:\s*(\w+)/) || [])[1] || r.product_id || '';
    const headLine = lines.find((l) => /^((?:EXTENDED |CONTINUED )?(?:ALERT|WARNING|WATCH|SUMMARY)|CANCEL(?: [A-Z]+)*):/i.test(l)) || '';
    const kind = (headLine.split(':')[0] || '').toLowerCase();
    const body = lines.slice(lines.indexOf(headLine) + 1)
      .filter((l) => l && !/^NOAA Space Weather Scale descriptions|^www\.swpc|^Comment:\s*$/i.test(l))
      .join('\n').slice(0, 900);
    out.push({ id: `${code}-${(text.match(/Serial Number:\s*(\d+)/) || [])[1] || issued}`, code, issued, kind, headline: headLine.replace(/^[^:]+:\s*/, ''), text: body });
  }
  out.sort((a, b) => b.issued.localeCompare(a.issued));
  return out.slice(0, max);
}

// The latest solar wind at L1, and the last day as 30-minute medians.
export function parseWind({ speed, mag, wind1m, mag1m } = {}, { now = Date.now() } = {}) {
  const s = rowsOf(speed)[0] || {};
  const m = rowsOf(mag)[0] || {};
  const latest = {
    t: iso(s.time_tag || m.time_tag),
    speed: num(s.proton_speed),
    bt: num(m.bt),
    bz: num(m.bz_gsm),
    density: null,
  };
  const bins = new Map();
  const add = (rows, pick) => {
    for (const r of rowsOf(rows)) {
      if (r.active === false) continue;
      const t = Date.parse(iso(r.time_tag));
      if (!Number.isFinite(t) || now - t > 86400000) continue;
      const k = Math.floor(t / 1800000) * 1800000;
      const b = bins.get(k) || { speed: [], density: [], bz: [], bt: [] };
      pick(r, b);
      bins.set(k, b);
    }
  };
  add(wind1m, (r, b) => { if (num(r.proton_speed) !== null) b.speed.push(num(r.proton_speed)); if (num(r.proton_density) !== null) b.density.push(num(r.proton_density)); });
  add(mag1m, (r, b) => { if (num(r.bz_gsm) !== null) b.bz.push(num(r.bz_gsm)); if (num(r.bt) !== null) b.bt.push(num(r.bt)); });
  const med = (a) => { if (!a.length) return null; const s2 = [...a].sort((x, y) => x - y); return Math.round(s2[Math.floor(s2.length / 2)] * 10) / 10; };
  const series = [...bins.entries()].sort((a, b) => a[0] - b[0])
    .map(([k, b]) => ({ t: new Date(k).toISOString(), speed: med(b.speed), density: med(b.density), bz: med(b.bz), bt: med(b.bt) }));
  for (let i = series.length - 1; i >= 0; i--) if (series[i].density !== null) { latest.density = series[i].density; break; }
  if (latest.speed === null && series.length) latest.speed = series[series.length - 1].speed;
  if (latest.bz === null && series.length) latest.bz = series[series.length - 1].bz;
  if (latest.speed === null && latest.bz === null) return null;
  return { latest, series };
}

// The numbered active regions on the latest day NOAA reported. NOAA rotates
// each day's observations to 2400 UT of that day, so the positions are
// valid at 00 UT the next day: `observed` is that moment. (Checked on
// 2026-10-12 against an SDO/HMI image taken at 13:41 UT: region 4548,
// S13W53 for "2026-10-12", sits at W47 there, as rotation back from 00 UT
// on the 13th puts it, and the Carrington longitudes agree with L0 then.)
// lonCM is degrees from the central meridian, west positive; carr the
// Carrington longitude.
export function parseRegions(list) {
  const rows = rowsOf(list).filter((r) => r.observed_date && num(r.region) !== null);
  if (!rows.length) return null;
  const day = rows.map((r) => r.observed_date).sort().pop();
  const regions = rows.filter((r) => r.observed_date === day).map((r) => ({
    region: num(r.region),
    location: r.location || null,
    lat: num(r.latitude),
    // NOAA's longitude field is east positive (S15E34 has 34): west positive here.
    lonCM: num(r.longitude) === null ? null : -num(r.longitude) || 0,
    carr: num(r.carrington_longitude),
    area: num(r.area),
    cls: r.spot_class || null,
    spots: num(r.number_spots),
    extent: num(r.extent),
    mag: r.mag_class || null,
    flares: { c: num(r.c_xray_events) || 0, m: num(r.m_xray_events) || 0, x: num(r.x_xray_events) || 0 },
    prob: { c: num(r.c_flare_probability), m: num(r.m_flare_probability), x: num(r.x_flare_probability) },
    first: iso(r.first_date),
  })).filter((r) => r.lat !== null && (r.carr !== null || r.lonCM !== null));
  regions.sort((a, b) => (b.area || 0) - (a.area || 0));
  return { day, observed: new Date(Date.parse(`${day}T00:00:00Z`) + 86400000).toISOString(), regions };
}

// The flare class letter and number to flux (W/m^2, 1-8 A).
export function flareFlux(cls) {
  const m = /^([ABCMX])(\d+(?:\.\d+)?)/.exec(String(cls || ''));
  if (!m) return null;
  return Number(({ A: 1e-8, B: 1e-7, C: 1e-6, M: 1e-5, X: 1e-4 }[m[1]] * Number(m[2])).toPrecision(3));
}

// The week's M and X flares from GOES, newest first, each matched to its
// NOAA region where the edited event list has it.
export function parseFlares(week, edited, latest) {
  const rows = rowsOf(week);
  const events = rowsOf(edited).filter((e) => e.type === 'XRA' && num(e.region));
  const flares = [];
  const seen = new Set();
  for (const r of rows) {
    const cls = r.max_class;
    if (!/^[MX]/.test(String(cls || ''))) continue;
    const max = iso(r.max_time);
    if (!max || seen.has(max)) continue;
    seen.add(max);
    const maxMs = Date.parse(max);
    const match = events.find((e) => Math.abs(Date.parse(iso(e.max_datetime)) - maxMs) <= 4 * 60000);
    flares.push({
      cls,
      begin: iso(r.begin_time),
      max,
      end: iso(r.end_time),
      flux: flareFlux(cls),
      region: match ? num(match.region) : null,
    });
  }
  flares.sort((a, b) => b.max.localeCompare(a.max));
  const now = rowsOf(latest)[0] || null;
  return {
    flares,
    current: now ? { t: iso(now.time_tag), cls: now.current_class || null } : null,
  };
}

// Helioviewer's answer for one image source -> the image's date and a URL
// for a full-disc PNG at the given size.
export function helioviewerImage(answer, { size = 1024 } = {}) {
  if (!answer || !answer.id || !answer.date) return null;
  // scale is the image's pixel size relative to the instrument's own
  // (0.6 arcsec): 4 puts the disc 800 px across in 1024.
  const scale = Math.round((4 * 1024) / size * 100) / 100;
  return {
    date: iso(answer.date),
    url: `https://api.helioviewer.org/v2/downloadImage/?id=${encodeURIComponent(answer.id)}&scale=${scale}&type=png&width=${size}&height=${size}`,
  };
}
